/**
 * A node's console: a shell on the node in the browser, over the relay channel method `node.shell.subscribe`
 * (`{ cols, rows }`, `input: true` to type; frames as a terminal's). It opens only when the person asks. Watching
 * needs the node's `shell:read` and typing `shell:write`, with a recent sign-in, both allowed in Settings.
 */

import type { ChannelCloseCode } from '@aioe/shared';
import { Terminal } from '@gravity-ui/icons';
import { useState } from 'react';

import { ArtEmpty, Button } from '../../components/ui.tsx';
import { useMachine } from '../machineContext.ts';
import { refusalOf } from '../relayChannel.ts';
import type { Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';
import { TerminalPane, type PaneState } from './TerminalPane.tsx';

const tone = 'teal' as const;

function consoleWords(code: ChannelCloseCode, reason?: string): string {
  const head = refusalOf(reason);
  if (head === 'shell_off') return 'The console is turned off on this node.';
  if (/method.not.found|unknown method|not_supported/i.test(reason ?? '') || head === 'method_not_found') return 'This node needs an update for its console.';
  if (code === 'scope' || head === 'shell_scope') return 'This node does not allow its console from here. Allow it in Settings.';
  if (code === 'done') return 'The shell has ended. Open it again to start a new one.';
  if (code === 'workbench_offline') return 'The node is not connected. This carries on when it is.';
  if (code === 'refused') return reason ? reason.replace(/^[a-z_]+:\s*/, '') : 'The node said no.';
  if (code === 'limit') return 'Too many open at once. Close one and open this again.';
  return 'The console closed.';
}

/** Who else has it open, when the node's answer says so: a name, an object with one, or a list of them. */
function othersOf(result: unknown): string | undefined {
  const r = (result ?? {}) as Record<string, unknown>;
  const name = (v: unknown) => (typeof v === 'string' ? v : v && typeof v === 'object' && typeof (v as { name?: unknown }).name === 'string' ? (v as { name: string }).name : undefined);
  const list = [r.usedBy, r.inUseBy, ...(Array.isArray(r.others) ? r.others : []), ...(Array.isArray(r.attached) ? r.attached : [])].map(name).filter((n): n is string => !!n);
  return list.length ? [...new Set(list)].join(', ') : undefined;
}

export function ConsoleView({ w }: { w: Workbench }) {
  const m = useMachine();
  const s = m.scopes;
  const [opened, setOpened] = useState(false);
  const [state, setState] = useState<PaneState>();
  const offered = s.available.includes('shell:read');
  const watch = s.has('shell:read');
  const others = state?.phase === 'live' ? othersOf(state.opened) : undefined;

  if (!s.known && !s.hidden) return <RemoteView><p className="text-sm text-muted">Reading what this node allows…</p></RemoteView>;
  if (!offered || !watch)
    return (
      <RemoteView narrow>
        <ArtEmpty
          tone={tone}
          icon={<Terminal />}
          title={offered ? 'The console is not allowed for this node' : 'The console is not available yet'}
          action={offered && !s.hidden ? <Button size="sm" variant="secondary" onPress={() => m.go('settings')}>Allow it in Settings</Button> : undefined}
        >
          {offered ? 'A shell can change anything on the node, so watching and typing are each allowed on purpose.' : 'A shell on this node in the browser arrives with a platform update.'}
        </ArtEmpty>
      </RemoteView>
    );
  return (
    <RemoteView
      fill
      actions={
        opened ? (
          <Button size="sm" variant="secondary" onPress={() => setOpened(false)}>
            Close
          </Button>
        ) : undefined
      }
    >
      {others ? <p className="m-0 text-sm text-muted">Being used by {others}</p> : null}
      {opened ? (
        <div className="min-h-[22rem] flex-1">
          <TerminalPane deviceId={w.deviceId} method="node.shell.subscribe" params={{}} type={s.has('shell:write')} writeScope="shell:write" words={consoleWords} onState={setState} />
        </div>
      ) : (
        <ArtEmpty tone={tone} icon={<Terminal />} title="A shell on this node" action={<Button size="sm" isDisabled={!w.connected} onPress={() => setOpened(true)}>Open the console</Button>}>
          {w.connected ? (s.has('shell:write') ? 'You can watch and type. Typing needs a recent sign-in.' : 'You can watch. Typing is allowed in Settings.') : 'The node is not connected right now.'}
        </ArtEmpty>
      )}
    </RemoteView>
  );
}
